/**
 * MyPerformanceDashboard Component
 * Employee's personal performance view
 */

import React from 'react';
import { clsx } from 'clsx';
import { useMyPerformance, useMyTrend } from '../../../hooks/usePerformanceData';
import { usePerformancePermissions } from '../../../hooks/usePerformancePermissions';
import { useAuth } from '../../../context/AuthContext';
import { PDFExportButton } from '../PDFExportButton';
import { PerformanceScoreCard } from '../PerformanceScoreCard';
import { PerformanceTrendChart } from '../PerformanceTrendChart';
import { PerformanceContributors } from '../PerformanceContributors';
import { OKRPerformanceTable } from '../OKRPerformanceTable';
import { ImprovementSuggestions } from '../ImprovementSuggestions';
import { PerformanceSummaryText } from '../PerformanceSummaryText';
import type { PerformanceQueryParams } from '../../../types';

export interface MyPerformanceDashboardProps {
  /** Period chosen by the page's master control. */
  queryParams: PerformanceQueryParams;
  onOKRClick?: (okrId: string) => void;
  className?: string;
}

export const MyPerformanceDashboard: React.FC<MyPerformanceDashboardProps> = ({
  queryParams,
  onOKRClick,
  className,
}) => {
  const { user } = useAuth();
  const permissions = usePerformancePermissions();
  const periodType = queryParams.periodType ?? 'quarterly';

  // Fetch performance data with proper params
  const {
    data: performance,
    isLoading: isPerformanceLoading,
    isError: isPerformanceError,
    error: performanceError,
  } = useMyPerformance(queryParams);

  // Fetch trend data
  const {
    data: trend,
    isLoading: isTrendLoading,
  } = useMyTrend({ ...queryParams, trendPeriods: 6 });

  const isLoading = isPerformanceLoading || isTrendLoading;
  const isError = isPerformanceError;
  const error = performanceError;

  if (isError) {
    return (
      <div className={clsx('p-6', className)}>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
          <p className="text-red-700">
            {error?.message || 'Failed to load performance data'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={clsx('space-y-6', className)}>
      {/* Header with Export (the period is chosen at page level) */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold text-stone-800">My Performance</h2>
          <p className="text-stone-500">
            Track your OKR progress and performance metrics
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {permissions.canExport && (
            <PDFExportButton
              type="individual"
              data={performance || null}
              periodType={periodType}
              userName={performance?.userName}
              tenantName={user?.organizationName}
              disabled={isLoading || !performance}
              variant="secondary"
              size="md"
            />
          )}
        </div>
      </div>

      {/* Summary Text */}
      {performance && (
        <PerformanceSummaryText
          summary={performance.summary}
          userName={performance.userName}
          periodLabel={performance.period.label}
          isLoading={isLoading}
        />
      )}

      {/* Score and Trend Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Score Card */}
        <div className="lg:col-span-1">
          <PerformanceScoreCard
            score={performance?.score.overall || 0}
            title="Overall Score"
            subtitle={performance?.period.label}
            size="lg"
            isLoading={isLoading}
          />
        </div>

        {/* Trend Chart */}
        <div className="lg:col-span-2">
          <PerformanceTrendChart
            data={trend || []}
            title="Score Trend"
            height={220}
            isLoading={isLoading}
          />
        </div>
      </div>

      {/* Breakdown Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Score Contributors */}
        <PerformanceContributors
          factors={performance?.factors || []}
          isLoading={isLoading}
        />

        {/* Improvement Suggestions */}
        <ImprovementSuggestions
          improvements={performance?.improvements || []}
          isLoading={isLoading}
        />
      </div>

      {/* OKR Table */}
      <OKRPerformanceTable
        okrs={performance?.okrs || []}
        onOKRClick={onOKRClick}
        isLoading={isLoading}
      />
    </div>
  );
};

export default MyPerformanceDashboard;
